<script setup>
import { ref, computed } from 'vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'toggle-active', 'sync']);
const isSyncing = ref(false);

const keywordsList = computed(() => {
  if (props.automation.matching_type === 'any') return ['Any comment'];
  return Array.isArray(props.automation.keywords) ? props.automation.keywords : [];
});

const handleToggle = () => {
  emit('toggle-active', props.automation);
};

const handleSync = () => {
  isSyncing.value = true;
  emit('sync', props.automation);
  setTimeout(() => {
    isSyncing.value = false;
  }, 2000);
};
</script>

<template>
  <div
    class="flex flex-col md:flex-row items-start md:items-center justify-between p-4 bg-n-surface-1 rounded-xl border border-n-weak hover:border-n-slate-6 transition-all gap-4 shadow-sm"
  >
    <!-- Left details -->
    <div class="flex items-start gap-3.5 flex-1 min-w-0">
      <!-- Target Thumbnail or Icon -->
      <div class="relative w-12 h-12 rounded-lg bg-n-surface-2 overflow-hidden border border-n-weak shrink-0 flex items-center justify-center">
        <img
          v-if="automation.target_media_url"
          :src="automation.target_media_url"
          :alt="automation.name"
          class="w-full h-full object-cover"
        />
        <span
          v-else-if="automation.post_type === 'all'"
          class="i-lucide-layers text-xl text-n-brand"
        />
        <span
          v-else
          class="i-lucide-image text-xl text-n-slate-10"
        />
      </div>

      <!-- Info -->
      <div class="flex flex-col min-w-0">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12 truncate">
            {{ automation.name }}
          </h3>
          <span
            class="text-[10px] px-2 py-0.5 rounded-full font-medium"
            :class="
              automation.active
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : 'bg-n-slate-4 text-n-slate-11 border border-n-weak'
            "
          >
            {{ automation.active ? 'Active' : 'Paused' }}
          </span>
        </div>

        <p class="text-xs text-n-slate-11 mt-1 truncate">
          <span class="font-medium text-n-slate-12">Scope:</span>
          {{ automation.post_type === 'specific' ? (automation.target_media_caption || 'Specific post/reel') : 'All posts and reels' }}
        </p>

        <!-- Keywords pills -->
        <div class="flex items-center gap-1.5 flex-wrap mt-2">
          <span class="text-[11px] text-n-slate-10">Keywords:</span>
          <span
            v-for="kw in keywordsList.slice(0, 4)"
            :key="kw"
            class="text-[10px] px-1.5 py-0.5 rounded bg-n-surface-2 text-n-slate-11 border border-n-weak"
          >
            {{ kw }}
          </span>
          <span v-if="keywordsList.length > 4" class="text-[10px] text-n-slate-10">
            +{{ keywordsList.length - 4 }} more
          </span>
        </div>
      </div>
    </div>

    <!-- Right Actions & Stats -->
    <div class="flex items-center gap-4 self-end md:self-center shrink-0">
      <!-- Active Switch -->
      <div class="flex items-center gap-2">
        <Switch
          :model-value="automation.active"
          size="sm"
          @update:model-value="handleToggle"
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-1">
        <Button
          :icon="isSyncing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'"
          size="sm"
          variant="ghost"
          title="Fetch & process comments now"
          @click="handleSync"
        />
        <Button
          icon="i-lucide-pencil"
          size="sm"
          variant="ghost"
          @click="emit('edit', automation)"
        />
        <Button
          icon="i-lucide-trash-2"
          size="sm"
          variant="ghost"
          color="ruby"
          @click="emit('delete', automation)"
        />
      </div>
    </div>
  </div>
</template>
